import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Button, Alert, CircularProgress,
  Paper, Divider, Grid, SelectChangeEvent
} from '@mui/material';
import { ColumnsResponse, ErrorResponse } from '../types';

interface ColumnMappingProps {
  filename: string;
  sheetName: string;
  isShopQL: boolean;
  onComplete: (sheetName: string, mapping: any) => void;
  onBack: () => void;
  currentSheet: number;
  totalSheets: number;
}

const ColumnMapping: React.FC<ColumnMappingProps> = ({
  filename,
  sheetName,
  isShopQL,
  onComplete,
  onBack,
  currentSheet,
  totalSheets
}) => {
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateColumn, setStateColumn] = useState('');
  const [dateColumn, setDateColumn] = useState('');
  const [hasRepeats, setHasRepeats] = useState(false);
  const [hasTotal, setHasTotal] = useState(true);
  const [totalColumn, setTotalColumn] = useState('');
  const [qtyColumn, setQtyColumn] = useState('');
  const [priceColumn, setPriceColumn] = useState('');
  const [isWholesale, setIsWholesale] = useState(false);
  const [isMarketplace, setIsMarketplace] = useState(false);

  useEffect(() => {
    // If ShopQL, autofill with the standard column mappings
    if (isShopQL) {
      setStateColumn('Shipping state');
      setDateColumn('Order date');
      setHasRepeats(false);
      setHasTotal(true);
      setTotalColumn('Order total');
      setIsWholesale(false);
      setIsMarketplace(false);
      setLoading(false);
      return;
    }

    const fetchColumns = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.post<ColumnsResponse | ErrorResponse>('/api/upload/columns', {
          filename,
          sheet: sheetName
        });
        
        const data = response.data;
        if ('error' in data) {
          setError(data.error);
        } else {
          setColumns(data.columns);
          
          // Set defaults if possible
          if (data.columns.length > 0) {
            // Try to find common column names
            const stateCol = data.columns.find((col: string) => 
              col.toLowerCase().includes('state')
            );
            const dateCol = data.columns.find((col: string) =>
              col.toLowerCase().includes('date')
            );
            const totalCol = data.columns.find((col: string) =>
              col.toLowerCase().includes('total') || 
              col.toLowerCase().includes('amount')
            );
            
            if (stateCol) setStateColumn(stateCol);
            if (dateCol) setDateColumn(dateCol);
            if (totalCol) setTotalColumn(totalCol);
          }
        }
      } catch (err) {
        console.error('Error fetching columns:', err);
        setError('Failed to load columns. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchColumns();
  }, [filename, sheetName, isShopQL]);

  const handleSubmit = () => {
    if (!stateColumn) {
      setError('State column is required');
      return;
    }
    
    if (!dateColumn) {
      setError('Date column is required');
      return;
    }
    
    if (hasTotal && !totalColumn) {
      setError('Please select a total column');
      return;
    }
    
    if (!hasTotal && (!qtyColumn || !priceColumn)) {
      setError('Please select both quantity and price columns');
      return;
    }
    
    const mapping = {
      state: stateColumn,
      date: dateColumn,
      hasRepeats,
      hasTotal,
      isWholesale,
      isMarketplace
    };
    
    if (hasTotal) {
      Object.assign(mapping, { totalColumn });
    } else {
      Object.assign(mapping, { qtyColumn, priceColumn });
    }
    
    onComplete(sheetName, mapping);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>
          Loading columns from {sheetName}...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Configure Data Mapping
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom>
        Sheet {currentSheet} of {totalSheets}: <strong>{sheetName}</strong>
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Required Fields
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6} component="div">
            <FormControl fullWidth required>
              <InputLabel>State Column</InputLabel>
              <Select
                value={stateColumn}
                onChange={(e: SelectChangeEvent) => setStateColumn(e.target.value)}
                label="State Column"
              >
                {columns.map(col => (
                  <MenuItem key={col} value={col}>{col}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6} component="div">
            <FormControl fullWidth required>
              <InputLabel>Order Date Column</InputLabel>
              <Select
                value={dateColumn}
                onChange={(e: SelectChangeEvent) => setDateColumn(e.target.value)}
                label="Order Date Column"
              >
                {columns.map(col => (
                  <MenuItem key={col} value={col}>{col}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          Order Amount Configuration
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={hasTotal}
              onChange={(e) => setHasTotal(e.target.checked)}
            />
          }
          label="Data includes order total column"
          sx={{ mb: 2 }}
        />

        {hasTotal ? (
          <FormControl fullWidth required>
            <InputLabel>Order Total Column</InputLabel>
            <Select
              value={totalColumn}
              onChange={(e: SelectChangeEvent) => setTotalColumn(e.target.value)}
              label="Order Total Column"
            >
              {columns.map(col => (
                <MenuItem key={col} value={col}>{col}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} component="div">
              <FormControl fullWidth required>
                <InputLabel>Quantity Column</InputLabel>
                <Select
                  value={qtyColumn}
                  onChange={(e: SelectChangeEvent) => setQtyColumn(e.target.value)}
                  label="Quantity Column"
                >
                  {columns.map(col => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} component="div">
              <FormControl fullWidth required>
                <InputLabel>Price Column</InputLabel>
                <Select
                  value={priceColumn}
                  onChange={(e: SelectChangeEvent) => setPriceColumn(e.target.value)}
                  label="Price Column"
                >
                  {columns.map(col => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          Additional Options
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4} component="div">
            <FormControlLabel
              control={
                <Switch
                  checked={hasRepeats}
                  onChange={(e) => setHasRepeats(e.target.checked)}
                />
              }
              label="Data has repeating orders"
            />
          </Grid>
          <Grid item xs={12} md={4} component="div">
            <FormControlLabel
              control={
                <Switch
                  checked={isWholesale}
                  onChange={(e) => setIsWholesale(e.target.checked)}
                />
              }
              label="Wholesale orders"
            />
          </Grid>
          <Grid item xs={12} md={4} component="div">
            <FormControlLabel
              control={
                <Switch
                  checked={isMarketplace}
                  onChange={(e) => setIsMarketplace(e.target.checked)}
                />
              }
              label="Marketplace orders"
            />
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack}>
          Back
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleSubmit}
        >
          {currentSheet < totalSheets ? 'Next Sheet' : 'Continue to Analysis'}
        </Button>
      </Box>
    </Box>
  );
};

export default ColumnMapping;